import { useEffect, useCallback, useRef, useState, lazy, Suspense, useMemo } from "react";
import { motion, MotionConfig } from "framer-motion";
import { Link, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { z } from "zod";
import { Carousel, CarouselContent, CarouselItem, CarouselNext, CarouselPrevious, type CarouselApi } from "@/components/ui/carousel";
import Autoplay from "embla-carousel-autoplay";
import { Check, ArrowRight, Users, QrCode, Link2, Star, Clock, UserPlus, Globe, Briefcase, Building2, Share2, BadgeCheck, Zap, Home, Calculator, MessageSquare, Image, FileText, ChevronDown, User, ChevronLeft, ChevronRight, X, Settings, Headphones, ShieldCheck, Lightbulb, ExternalLink, Mail, Monitor } from "lucide-react";
import qcardLogo from "@/assets/qcard-logo-official.png";
import companyLogos from "@/assets/company-logos.png";
import testimonialAaron from "@/assets/testimonial-aaron-pyman.webp";
import testimonialTodd from "@/assets/testimonial-todd-edwards.webp";
import testimonialNikki from "@/assets/testimonial-nikki-beauchamp.webp";
import testimonialAnis from "@/assets/testimonial-anis-dave.webp";
import demoArrow from "@/assets/demo-arrow.png";
import InteractivePhoneMockup from "@/components/landing/InteractivePhoneMockup";
import { LazyVideo } from "@/components/landing/LazyVideo";
import { LazyImage } from "@/components/landing/LazyImage";
import { DeferredSignupIframe } from "@/components/landing/DeferredSignupIframe";
import { useIsMobile } from "@/hooks/use-mobile";
import { useIsDesktop, useReducedMotion } from "@/components/landing/DesktopOptimizations";
import featurePhotoContactVideo from "@/assets/rich-profile-content-demo.mp4";
import featureZoomBackground from "@/assets/feature-zoom-background.png";
import analyticsDemo from "@/assets/analytics-demo.gif";
import socialLinksDemo from "@/assets/social-links-demo.gif";
import tapToContactDemo from "@/assets/tap-to-contact-demo.gif";
import colleaguesDemo from "@/assets/colleagues-demo.gif";
import qrCodeDemo from "@/assets/qr-code-demo.gif";
import photoContactDemo from "@/assets/feature-photo-contact-animation.gif";
import photoContactVideoNew from "@/assets/feature-photo-contact-video.mp4";
import analyticsVideoNew from "@/assets/analytics-final.mp4";
import socialLinksVideoNew from "@/assets/social-media-links-v2.mp4";

// Lazy load heavy components for below-the-fold content
const ComparisonGridSection = lazy(() => import("@/components/landing/ComparisonGridSection"));

// Video imports - lazy loaded via LazyVideo component
import featureAnalyticsVideo from "@/assets/feature-analytics-video.mp4";
import featureSocialLinksVideo from "@/assets/social-links-final.mp4";
import featureQrCodeVideo from "@/assets/qr-code-video.mp4";
import featureAddToContactsVideo from "@/assets/add-to-contacts-video.mp4";
import featureRichProfileVideo from "@/assets/rich-profile-content-video.mp4";
import featureTeamDirectoryVideo from "@/assets/colleagues-video.mp4";
import featureTapToContactVideo from "@/assets/tap-to-contact-video.mp4";
import featureEmailFooterImage from "@/assets/email-footer-image.png";
import featureZoomBackgroundVideo from "@/assets/zoom-background-video.mp4";

// Feature image dimensions for CLS prevention
const FEATURE_IMAGE_DIMENSIONS = { width: 662, height: 662 };

const features = [{
  icon: Image,
  title: "Photo & Contact Info",
  description: "Professional headshot with all your contact details, Company role & About me section",
  image: photoContactVideoNew,
  isVideo: true
}, {
  icon: Briefcase,
  title: "Analytics",
  description: "Track networking impact with profile views, sources, and saved contacts",
  image: analyticsVideoNew,
  isVideo: true
}, {
  icon: Globe,
  title: "Social & Website Links",
  description: "Connect all your platforms in one place",
  image: socialLinksVideoNew,
  isVideo: true
}, {
  icon: QrCode,
  title: "QR Code",
  description: "Instant sharing with a simple scan",
  image: featureQrCodeVideo,
  isVideo: true
}, {
  icon: Link2,
  title: "Tap To Contact",
  description: "More contact options increase customer engagement rates",
  image: featureTapToContactVideo,
  isVideo: true
}, {
  icon: UserPlus,
  title: "Instant Add-to-Contacts",
  description: "One tap to save you to their phone",
  image: featureAddToContactsVideo,
  isVideo: true
}, {
  icon: FileText,
  title: "Rich Profile Content",
  description: "Tailor your profile to your needs",
  image: featureRichProfileVideo,
  isVideo: true
}, {
  icon: Share2,
  title: "Colleagues",
  description: "Show team members and access QCard profiles",
  image: colleaguesDemo
}, {
  icon: Mail,
  title: "Email Footers",
  description: "Add your QCard link to every email signature",
  image: featureEmailFooterImage,
  isVideo: false
}, {
  icon: Monitor,
  title: "Video Conference Background",
  description: "Brand your video calls with your QCard QR code",
  image: featureZoomBackgroundVideo,
  isVideo: true
}];
const benefits = [{
  icon: BadgeCheck,
  title: "Look Polished & Credible",
  description: "Make a professional first impression every time"
}, {
  icon: Zap,
  title: "Impress in Seconds",
  description: "Stand out with a modern, sleek digital presence"
}, {
  icon: Globe,
  title: "Works Anywhere",
  description: "Share via QR, link, or text — no app required"
}, {
  icon: UserPlus,
  title: "Get Saved Instantly",
  description: "Your contacts save you to their phone with one tap"
}];
const audiences = [{
  icon: Users,
  title: "Individuals",
  description: "Anyone who wants to network smarter",
  link: "https://www.qcardenterprise.com/4-reasons-why-every-professional-needs-a-digital-business-card"
}, {
  icon: Home,
  title: "Real Estate Agents",
  description: "Close deals with instant follow-up",
  link: "https://www.qcardenterprise.com/how-nfc-smart-business-cards-improve-client-trust-in-real-estate-and-finance"
}, {
  icon: MessageSquare,
  title: "Consultants",
  description: "Build credibility from the first meeting",
  link: "https://www.qcardenterprise.com/consulting-confidence-5-essential-elements-every-consultant-needs-on-their-digital-business-card"
}, {
  icon: Calculator,
  title: "Financial Professionals",
  description: "Establish trust with a polished profile",
  link: "https://www.qcardenterprise.com/building-trust-in-the-digital-age-essential-elements-for-a-financial-advisors-digital-business-card"
}, {
  icon: Briefcase,
  title: "Lawyers",
  description: "Present a professional image to clients",
  link: "https://www.qcardenterprise.com/digital-business-cards-for-lawyers-how-to-impress-clients-with-tech"
}, {
  icon: Building2,
  title: "Enterprises",
  description: "Equip your entire team with digital cards",
  link: "https://www.qcardenterprise.com/enterprise-branding-in-the-digital-age-why-every-team-member-deserves-a-profile"
}];
const enterpriseFeatures = [{
  icon: Settings,
  title: "Integrates With Your Enterprise",
  color: "bg-blue-100 text-blue-600",
  items: ["Integrations with enterprise HRMS and CRM systems", "Unified and controlled platform to manage employee network and access", "APIs", "Custom URLs tailored to your company's domain"]
}, {
  icon: Headphones,
  title: "Enterprise-Level Customer Support",
  color: "bg-orange-100 text-orange-500",
  items: ["Seamless and quick user onboarding and system implementation", "Employee training on how to use the platform", "24/7 Helpdesk", "360-Degree Customer Success Team", "Personal Concierge", "Rollout support"]
}, {
  icon: ShieldCheck,
  title: "Secure",
  color: "bg-pink-100 text-pink-500",
  items: ["Single Sign-On", "Enterprise-grade security", "On Prem/Cloud Solutions"]
}, {
  icon: Lightbulb,
  title: "Custom Solutions",
  color: "bg-green-100 text-green-500",
  items: ["Collaborate with the QCard team to tailor solutions for the specific needs of your enterprise", "In-house experts for expanded customer experience solutions"]
}];
const steps = [{
  step: "01",
  title: "Create Your Free QCard",
  description: "Sign up in seconds — no credit card needed"
}, {
  step: "02",
  title: "Customize Your Profile",
  description: "Add your photo, bio, links, and contact info"
}, {
  step: "03",
  title: "Share Instantly",
  description: "Use your QR code or personal link anywhere"
}];
const testimonials = [{
  image: testimonialAaron,
  name: "Aaron Pyman",
  company: "Redfin",
  link: "https://www.linkedin.com/in/aaronpyman/"
}, {
  image: testimonialAnis,
  name: "Anis Dave",
  company: "from - The Digital Transformation Agency",
  link: "https://www.linkedin.com/in/aaronpyman/"
}, {
  image: testimonialNikki,
  name: "Nikki Beauchamp",
  company: "Sotheby's International Realty",
  link: "https://www.linkedin.com/in/nicolebeauchamp/"
}, {
  image: testimonialTodd,
  name: "Todd Edwards",
  company: "Redfin",
  link: "https://www.linkedin.com/in/rtoddedwards/"
}];
const profileOptions = [{
  id: "realEstate",
  label: "Real Estate",
  url: "https://card.qcardenterprise.com/c/michaela_zuckerberg_427545949"
}, {
  id: "consulting",
  label: "Consulting",
  url: "https://card.qcardenterprise.com/c/test_capgemini_1228503402"
}, {
  id: "law",
  label: "Law",
  url: "https://card.qcardenterprise.com/c/test_lplegal_1021874569"
}, {
  id: "accounting",
  label: "Accounting",
  url: "https://card.qcardenterprise.com/c/fiona_grossman_1093779199"
}] as const;
const PROFILE_IFRAME_WIDTH = 375;
const DEFAULT_PROFILE_IFRAME_HEIGHT = 4200;
const PROFILE_HEIGHT_STEP = 1200;
const PROFILE_MAX_IFRAME_HEIGHT = 14000;
const HomepageEnterprise = () => {
  const [selectedFeature, setSelectedFeature] = useState(0);
  const [selectedProfile, setSelectedProfile] = useState(0);
  const [isEnterpriseExpanded, setIsEnterpriseExpanded] = useState(false);
  const [profileHeights, setProfileHeights] = useState<Record<string, number>>(() => Object.fromEntries(profileOptions.map(p => [p.id, DEFAULT_PROFILE_IFRAME_HEIGHT])));
  const dragExpandLockRef = useRef(false);
  const profileIframeRef = useRef<HTMLIFrameElement | null>(null);
  const phoneScreenRef = useRef<HTMLDivElement | null>(null);
  const [profileScale, setProfileScale] = useState(0.78);
  const activeProfile = profileOptions[selectedProfile];
  const activeProfileHeight = profileHeights[activeProfile.id] ?? DEFAULT_PROFILE_IFRAME_HEIGHT;

  // Desktop performance optimization hooks
  const isDesktop = useIsDesktop();
  const prefersReducedMotion = useReducedMotion();
  const isMobile = useIsMobile();
  
  // Optimized animation config for desktop - reduces TBT by simplifying animations
  const shouldReduceMotion = prefersReducedMotion ? "always" as const : "never" as const;
  const desktopTransition = useMemo(() => 
    isDesktop ? { 
      type: "tween" as const, 
      duration: 0.2,
      ease: [0.25, 0.1, 0.25, 1] as const, // Use easing array for proper typing
    } : undefined
  , [isDesktop]);

  // Dynamically calculate scale based on phone screen width
  useEffect(() => {
    const updateScale = () => {
      if (phoneScreenRef.current) {
        const containerWidth = phoneScreenRef.current.offsetWidth;
        const newScale = containerWidth / PROFILE_IFRAME_WIDTH;
        setProfileScale(newScale);
      }
    };
    updateScale();
    window.addEventListener("resize", updateScale);
    return () => window.removeEventListener("resize", updateScale);
  }, []);
  const [isSubmitting, setIsSubmitting] = useState(false);
  // Store form load timestamp for anti-replay protection
  const [formLoadTime] = useState(() => Date.now());
  // Honeypot field for bot detection (should remain empty)
  const [honeypot, setHoneypot] = useState("");
  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
    title: "",
    company: ""
  });
  const navigate = useNavigate();
  const {
    toast
  } = useToast();

  // Best-effort auto sizing: if the embedded profile page posts its height via postMessage,
  // we use it as the iframe height for the currently selected profile.
  useEffect(() => {
    const ALLOWED_IFRAME_ORIGIN = "https://card.qcardenterprise.com";
    const MIN_HEIGHT = 300;
    const MAX_HEIGHT = 20000;
    
    const onMessage = (event: MessageEvent) => {
      // Validate the message source is our iframe
      if (event.source !== profileIframeRef.current?.contentWindow) return;
      
      // Validate the origin to prevent cross-origin attacks
      if (!event.origin || !event.origin.startsWith(ALLOWED_IFRAME_ORIGIN)) return;
      
      const data: unknown = event.data;
      
      // Validate data structure and extract height
      if (typeof data !== "object" || data === null || !("height" in data)) return;
      
      const height = Number((data as { height: unknown }).height);
      
      // Validate height is a reasonable value (between MIN_HEIGHT and MAX_HEIGHT)
      if (!Number.isFinite(height) || height < MIN_HEIGHT || height > MAX_HEIGHT) return;
      
      setProfileHeights(prev => {
        const current = prev[activeProfile.id] ?? DEFAULT_PROFILE_IFRAME_HEIGHT;
        const next = Math.min(PROFILE_MAX_IFRAME_HEIGHT, Math.max(current, height));
        if (next === current) return prev;
        return {
          ...prev,
          [activeProfile.id]: next
        };
      });
    };
    window.addEventListener("message", onMessage);
    return () => window.removeEventListener("message", onMessage);
  }, [activeProfile.id]);
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const {
      name,
      value
    } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  // Zod schema for form validation
  const signupSchema = z.object({
    firstName: z.string().min(1, "First name is required").max(100, "First name too long"),
    lastName: z.string().min(1, "Last name is required").max(100, "Last name too long"),
    email: z.string().email("Invalid email address").max(200, "Email too long"),
    phone: z.string().max(20, "Phone number too long").optional().or(z.literal("")),
    title: z.string().max(100, "Title too long").optional().or(z.literal("")),
    company: z.string().max(200, "Company name too long").optional().or(z.literal("")),
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Validate form data with Zod
    const validationResult = signupSchema.safeParse(formData);
    if (!validationResult.success) {
      const firstError = validationResult.error.errors[0];
      toast({
        title: "Validation Error",
        description: firstError?.message || "Please check your input and try again.",
        variant: "destructive"
      });
      return;
    }

    const validatedData = validationResult.data;
    setIsSubmitting(true);
    
    // Create abort controller for timeout handling
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 10000); // 10 second timeout
    
    try {
      // Submit to external endpoint with validated data
      const response = await fetch("https://card.qcardenterprise.com/createNewUserAccount", {
        method: "POST",
        headers: {
          "Content-Type": "application/json"
        },
        body: JSON.stringify({
          firstName: validatedData.firstName,
          lastName: validatedData.lastName,
          email: validatedData.email,
          phone: validatedData.phone || "",
          title: validatedData.title || "",
          company: validatedData.company || ""
        }),
        signal: controller.signal
      });

      // Validate HTTP response status
      if (!response.ok) {
        if (import.meta.env.DEV) {
          console.error('[Server] External API error:', response.status);
        }
        throw new Error(`Account creation failed (${response.status}). Please try again.`);
      }

      // Also send notification email via edge function with validated data + security fields
      const emailResult = await supabase.functions.invoke("send-signup-email", {
        body: {
          ...validatedData,
          _hp: honeypot, // Honeypot field for bot detection
          _ts: formLoadTime // Timestamp for replay protection
        }
      });

      // Check if edge function call failed
      if (emailResult.error) {
        if (import.meta.env.DEV) {
          console.error('[Server] Email notification error:', emailResult.error);
        }
        // Continue to confirmation since account was created successfully
        // but log the email failure
      }

      // Navigate to confirmation page only after successful account creation
      navigate("/signup-confirmation");
    } catch (error: unknown) {
      // Handle abort/timeout errors specifically
      if (error instanceof Error && error.name === 'AbortError') {
        toast({
          title: "Request Timeout",
          description: "The request took too long. Please check your connection and try again.",
          variant: "destructive"
        });
      } else {
        // Only log errors in development to avoid leaking details in production
        if (import.meta.env.DEV) {
          console.error("Error submitting form:", error);
        }
        toast({
          title: "Submission Error",
          description: error instanceof Error ? error.message : "There was a problem submitting your information. Please try again.",
          variant: "destructive"
        });
      }
    } finally {
      clearTimeout(timeoutId);
      setIsSubmitting(false);
    }
  };
  const getEmbedParam = () => {
    if (typeof window === "undefined") return null;
    const direct = new URLSearchParams(window.location.search).get("embed");
    if (direct) return direct;

    // When loaded via HashRouter, the query string is inside the hash:
    // e.g. /#/homepage-enterprise?embed=1
    const hash = window.location.hash;
    const queryIndex = hash.indexOf("?");
    if (queryIndex === -1) return null;
    return new URLSearchParams(hash.slice(queryIndex + 1)).get("embed");
  };
  const isEmbed = getEmbedParam() === "1";

  // Send page height to parent window for dynamic iframe sizing (Webflow, etc.)
  // On mobile: allow native scrolling inside the iframe (most reliable)
  // On desktop: optionally forward wheel events for seamless parent scrolling
  useEffect(() => {
    if (!isEmbed) return;
    
    // Detect if mobile/touch device
    const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent) || 
                     ('ontouchstart' in window) ||
                     (navigator.maxTouchPoints > 0);
    
    const prevHtmlOverflow = document.documentElement.style.overflow;
    const prevBodyOverflow = document.body.style.overflow;
    const prevUserSelect = document.body.style.userSelect;
    
    // On mobile: ALLOW native scrolling inside iframe - this is the most reliable approach
    // On desktop: we can try to forward scroll, but keep fallback scrolling enabled
    if (isMobile) {
      // Enable smooth scrolling on mobile
      document.documentElement.style.overflow = "auto";
      document.body.style.overflow = "auto";
      document.documentElement.style.setProperty("-webkit-overflow-scrolling", "touch");
      document.body.style.setProperty("-webkit-overflow-scrolling", "touch");
    } else {
      // On desktop, still allow scrolling as fallback
      document.documentElement.style.overflow = "auto";
      document.body.style.overflow = "auto";
    }
    
    document.documentElement.classList.add("scrollbar-hide");
    document.body.classList.add("scrollbar-hide");
    
    let raf = 0;
    const getHeight = () => Math.max(document.documentElement.scrollHeight, document.body.scrollHeight, document.documentElement.offsetHeight, document.body.offsetHeight);
    const postHeight = () => {
      const height = getHeight();
      window.parent.postMessage({
        type: "qcard-height",
        height
      }, "*");
    };
    const sendHeight = () => {
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(postHeight);
    };
    const postScroll = (deltaY: number, deltaX: number) => {
      window.parent.postMessage({
        type: "qcard-scroll",
        deltaY,
        deltaX
      }, "*");
    };
    const onMessage = (event: MessageEvent) => {
      if (event?.data?.type === "qcard-request-height") {
        sendHeight();
      }
    };
    
    // Only forward wheel events on desktop (not mobile)
    const onWheel = (event: WheelEvent) => {
      // Don't prevent default - let iframe scroll naturally as fallback
      postScroll(event.deltaY, event.deltaX);
    };

    // Click + drag to scroll (desktop only)
    const isInteractiveTarget = (target: EventTarget | null) => {
      const el = target as HTMLElement | null;
      if (!el) return false;
      return Boolean(el.closest("a, button, input, textarea, select, label, summary, [role='button'], [data-no-drag-scroll]"));
    };
    let isDragging = false;
    let lastMouseX = 0;
    let lastMouseY = 0;
    const onMouseDown = (event: MouseEvent) => {
      if (isMobile) return; // Skip on mobile
      if (event.button !== 0) return;
      if (isInteractiveTarget(event.target)) return;
      isDragging = true;
      lastMouseX = event.clientX;
      lastMouseY = event.clientY;
      document.body.style.userSelect = "none";
    };
    const onMouseMove = (event: MouseEvent) => {
      if (!isDragging) return;
      event.preventDefault();
      const deltaY = lastMouseY - event.clientY;
      const deltaX = lastMouseX - event.clientX;
      lastMouseX = event.clientX;
      lastMouseY = event.clientY;
      if (deltaY !== 0 || deltaX !== 0) postScroll(deltaY, deltaX);
    };
    const endDrag = () => {
      if (!isDragging) return;
      isDragging = false;
      document.body.style.userSelect = prevUserSelect;
    };

    // Initial + delayed sends so the parent listener has time to attach
    sendHeight();
    const t1 = window.setTimeout(sendHeight, 150);
    const t2 = window.setTimeout(sendHeight, 600);
    const t3 = window.setTimeout(sendHeight, 1500);
    window.addEventListener("resize", sendHeight);
    window.addEventListener("load", sendHeight);
    window.addEventListener("message", onMessage);
    
    // Only add wheel/drag listeners on desktop
    if (!isMobile) {
      window.addEventListener("wheel", onWheel, { passive: true });
      window.addEventListener("mousedown", onMouseDown, { passive: true });
      window.addEventListener("mousemove", onMouseMove, { passive: false });
      window.addEventListener("mouseup", endDrag, { passive: true });
      window.addEventListener("blur", endDrag);
    }
    
    const resizeObserver = new ResizeObserver(sendHeight);
    resizeObserver.observe(document.documentElement);
    
    return () => {
      document.documentElement.style.overflow = prevHtmlOverflow;
      document.body.style.overflow = prevBodyOverflow;
      document.documentElement.classList.remove("scrollbar-hide");
      document.body.classList.remove("scrollbar-hide");
      document.body.style.userSelect = prevUserSelect;
      cancelAnimationFrame(raf);
      window.clearTimeout(t1);
      window.clearTimeout(t2);
      window.clearTimeout(t3);
      window.removeEventListener("resize", sendHeight);
      window.removeEventListener("load", sendHeight);
      window.removeEventListener("message", onMessage);
      if (!isMobile) {
        window.removeEventListener("wheel", onWheel as any);
        window.removeEventListener("mousedown", onMouseDown as any);
        window.removeEventListener("mousemove", onMouseMove as any);
        window.removeEventListener("mouseup", endDrag as any);
        window.removeEventListener("blur", endDrag as any);
      }
      resizeObserver.disconnect();
    };
  }, [isEmbed]);

  // Add embed-mode class to root element for CSS reset
  useEffect(() => {
    if (isEmbed) {
      document.getElementById('root')?.classList.add('embed-mode');
      document.documentElement.style.height = 'auto';
      document.body.style.height = 'auto';
    }
    return () => {
      document.getElementById('root')?.classList.remove('embed-mode');
    };
  }, [isEmbed]);
  return (
    <MotionConfig reducedMotion={shouldReduceMotion} transition={desktopTransition}>
      <div className={`bg-background text-foreground desktop-optimized ${isEmbed ? "scrollbar-hide" : "min-h-screen"}`} style={isEmbed ? {
        scrollbarWidth: "none",
        msOverflowStyle: "none",
        minHeight: 'auto',
        height: 'auto'
      } : undefined} role="main">
      {/* Hero Section */}
      <section id="signup-section" className="relative overflow-hidden">
        {/* Background gradient */}
        <div className="absolute inset-0 bg-gradient-to-br from-secondary/50 via-background to-accent/30" />
        
        {/* Large gradient orbs - CSS animations instead of framer-motion for performance */}
        <div 
          className="absolute top-0 right-0 w-[500px] h-[500px] bg-gradient-to-bl from-enterprise-purple/15 via-purple-200/10 to-transparent rounded-full blur-3xl -translate-y-1/3 translate-x-1/4 animate-[fadeIn_1.2s_ease-out]"
        />
        <div 
          className="absolute bottom-0 left-0 w-[400px] h-[400px] bg-gradient-to-tr from-orange-200/15 via-amber-100/10 to-transparent rounded-full blur-3xl translate-y-1/4 -translate-x-1/4 animate-[fadeIn_1.2s_ease-out_0.2s_both]"
        />
        
        {/* Subtle floating geometric shapes - CSS animations */}
        <div className="absolute top-20 left-[15%] w-2 h-2 bg-enterprise-purple/30 rounded-full animate-[fadeIn_0.8s_ease-out_0.5s_both]" />
        <div className="absolute top-32 right-[20%] w-3 h-3 bg-orange-300/25 rounded-full animate-[fadeIn_0.8s_ease-out_0.7s_both]" />
        <div className="absolute bottom-24 left-[25%] w-1.5 h-1.5 bg-purple-300/40 rounded-full animate-[fadeIn_0.8s_ease-out_0.6s_both]" />
        
        {/* Elegant accent lines - CSS animations */}
        <div className="absolute top-1/4 left-0 w-32 md:w-48 h-[1px] bg-gradient-to-r from-transparent via-enterprise-purple/30 to-transparent animate-[scaleXIn_1s_ease-out_0.4s_both] origin-left" />
        <div className="absolute bottom-1/3 right-0 w-24 md:w-40 h-[1px] bg-gradient-to-l from-transparent via-orange-300/25 to-transparent animate-[scaleXIn_1s_ease-out_0.6s_both] origin-right" />
        
        {/* Soft corner accents - CSS animations */}
        <div className="absolute top-12 right-12 w-20 h-20 border border-enterprise-purple/15 rounded-full animate-[fadeIn_1.2s_ease-out_0.3s_both]" />
        <div className="absolute bottom-16 left-8 w-16 h-16 border border-orange-200/10 rounded-lg rotate-12 animate-[fadeIn_1.2s_ease-out_0.5s_both]" />
        
        <div className="container relative z-10 mx-auto px-4 sm:px-6 py-16 md:py-24">
          <div className="grid lg:grid-cols-3 gap-8 lg:gap-6 items-start max-w-full overflow-hidden">
            {/* Left Content - Split into sections for mobile reordering */}
            <motion.div 
              className="text-center lg:text-left w-full max-w-full px-2 sm:px-0 lg:pt-24 order-1 lg:order-none flex flex-col"
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, ease: [0.25, 0.46, 0.45, 0.94] }}
            >
              {/* Top section - always visible first */}
              <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold leading-tight mb-2 text-foreground">
                Your First Impression,<br />
                <span className="text-gradient">Perfected.</span>
              </h1>
              <p className="text-lg md:text-xl text-muted-foreground mb-4">
                A free digital business card you can share instantly.
              </p>
              <motion.p 
                className="inline-flex items-center justify-center lg:justify-start gap-2 text-base font-bold mb-6 bg-gradient-to-r from-enterprise-purple to-enterprise-purple-dark bg-clip-text text-transparent w-full"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.6, delay: 0.2 }}
              >
                FREE Forever — No Credit Card Required
              </motion.p>
              
              {/* Mobile Phone Mockup - Only visible on mobile, positioned after FREE Forever */}
              <div className="lg:hidden flex flex-col items-center justify-start w-full max-w-full mb-8">
                <InteractivePhoneMockup defaultProfileIndex={selectedProfile} showProfileSwitcher={true} />
              </div>
              
              {/* Bottom section - appears after phone on mobile */}
              <motion.p 
                className="text-lg text-muted-foreground mb-4 max-w-xl mx-auto lg:mx-0"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.3 }}
              >
                From meetings to referrals to everyday introductions, QCard gives you a polished digital profile you can share instantly, making follow-up effortless and every opportunity count.
              </motion.p>
              
              <motion.div 
                className="mb-8 text-base text-muted-foreground max-w-xl mx-auto lg:mx-0"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.4 }}
              >
                <p className="font-medium text-foreground mb-2 text-lg">Designed for:</p>
                <ul className="space-y-1.5">
                  <li>• Meetings and introductions</li>
                  <li>• Events and follow-ups</li>
                  <li>• Anywhere connections matter</li>
                </ul>
              </motion.div>
              
              <motion.div 
                className="flex flex-wrap items-center gap-4 justify-center lg:justify-start text-base text-muted-foreground"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.5 }}
              >
                <div className="flex items-center gap-2">
                  <Check className="w-5 h-5 text-enterprise-purple" />
                  <span>Feature Rich</span>
                </div>
                <div className="flex items-center gap-2">
                  <Check className="w-5 h-5 text-enterprise-purple" />
                  <span>60-second setup</span>
                </div>
                <div className="flex items-center gap-2">
                  <Check className="w-5 h-5 text-enterprise-purple" />
                  <span>No Ads</span>
                </div>
              </motion.div>
            </motion.div>

            {/* Center - Interactive Phone Demo (Desktop only) */}
            <div className="hidden lg:flex flex-col items-center justify-start order-2 lg:order-none w-full max-w-full">
              <InteractivePhoneMockup defaultProfileIndex={selectedProfile} showProfileSwitcher={true} />
            </div>

            {/* Right - Sign Up Form (Embedded iframe with lazy loading for mobile performance) */}
            <div className="flex flex-col justify-start items-center lg:items-end w-full max-w-full order-3 lg:order-none">
              <div className="bg-card rounded-2xl p-4 shadow-2xl border border-border w-full max-w-md overflow-hidden mx-auto lg:mx-0">
                <div className="flex items-center justify-center min-h-[60px] mb-4">
                  <h2 className="text-xl font-bold text-center text-foreground">
                    Sign up for your<br />FREE Digital Business Card
                  </h2>
                </div>
                <DeferredSignupIframe
                  src="https://card.qcardenterprise.com/createNewUserAccount"
                  title="QCard signup form - Create your free digital business card"
                  height={630}
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Company Logos */}
      <section className="py-12 bg-muted/30 border-y border-border">
        <div className="container mx-auto px-4">
          <p className="text-center text-muted-foreground mb-8 text-sm font-medium">
            Trusted by professionals at leading companies
          </p>
          <img 
            src={companyLogos} 
            alt="Trusted by Redfin, Keller Williams, UBS, S&P Global, Sotheby's, Compass, and more" 
            className="max-w-4xl mx-auto w-full"
            width={1390}
            height={338}
            loading="lazy"
            decoding="async"
          />
        </div>
      </section>

      {/* SECTION 2: All Features. No Fees. */}
      <section className="py-20 md:py-28 bg-background">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold mb-4">
              All Features. <span className="text-gradient">No Fees.</span>
            </h2>
            <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
              Everything you need to create a professional digital presence — included free.
            </p>
          </div>

          <div className="hidden md:flex flex-col md:flex-row gap-8 md:gap-12 max-w-6xl mx-auto mt-8">
            {/* Left Side - Feature Titles Grid */}
            <div className="w-full md:w-2/5">
              <div className="grid grid-cols-2 gap-3">
                {features.map((feature, index) => <button key={index} onClick={() => setSelectedFeature(index)} className={`p-4 rounded-xl text-left transition-all duration-300 border ${selectedFeature === index ? 'bg-enterprise-purple text-white border-enterprise-purple shadow-lg' : 'bg-card border-border hover:border-enterprise-purple/50 hover:bg-muted/50'}`}>
                    <feature.icon className={`w-6 h-6 mb-2 ${selectedFeature === index ? 'text-white' : 'text-enterprise-purple'}`} />
                    <h3 className={`font-semibold text-sm ${selectedFeature === index ? 'text-white' : 'text-foreground'}`}>
                      {feature.title}
                    </h3>
                  </button>)}
              </div>
            </div>

            {/* Right Side - Selected Feature Image & Description */}
            <div className="w-full md:w-3/5">
              <div className="bg-muted/30 rounded-2xl overflow-hidden border border-border shadow-lg aspect-square">
                {features[selectedFeature].isVideo ? (
                  <LazyVideo 
                    src={features[selectedFeature].image} 
                    className="w-full h-full object-contain" 
                  />
                ) : (
                  <LazyImage 
                    src={features[selectedFeature].image} 
                    alt={features[selectedFeature].title} 
                    className="w-full h-full object-contain"
                    width={(features[selectedFeature] as any).dimensions?.width || FEATURE_IMAGE_DIMENSIONS.width}
                    height={(features[selectedFeature] as any).dimensions?.height || FEATURE_IMAGE_DIMENSIONS.height}
                  />
                )}
              </div>
              <div className="mt-6 text-center">
                <h3 className="text-xl md:text-2xl font-bold mb-3 text-foreground">
                  {features[selectedFeature].title}
                </h3>
                <p className="text-muted-foreground leading-relaxed">
                  {features[selectedFeature].description}
                </p>
              </div>
            </div>
          </div>

          {/* Mobile: Show all features stacked */}
          <div className="md:hidden mt-12 space-y-8">
            {features.map((feature, index) => <div key={index} className="text-center">
                <div className="bg-muted/30 rounded-2xl overflow-hidden border border-border shadow-lg mb-4 aspect-square">
                  {feature.isVideo ? (
                    <LazyVideo src={feature.image} className="w-full h-full object-contain" />
                  ) : (
                    <LazyImage 
                      src={feature.image} 
                      alt={feature.title} 
                      className="w-full h-full object-contain"
                      width={(feature as any).dimensions?.width || FEATURE_IMAGE_DIMENSIONS.width}
                      height={(feature as any).dimensions?.height || FEATURE_IMAGE_DIMENSIONS.height}
                    />
                  )}
                </div>
                <h3 className="text-xl font-bold mb-2 text-foreground">{feature.title}</h3>
                <p className="text-muted-foreground">{feature.description}</p>
              </div>)}
          </div>

        </div>
      </section>

      {/* SECTION: Who QCard Is For */}
      <section className="py-20 md:py-28 bg-muted/30">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold mb-4">
              Who Is QCard <span className="text-gradient">For?</span>
            </h2>
            <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
              Professionals across every industry use QCard to make lasting connections.
            </p>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 max-w-5xl mx-auto">
            {audiences.map((audience, index) => {
              const CardContent = (
                <>
                  <div className="w-12 h-12 rounded-xl bg-enterprise-purple/10 flex items-center justify-center flex-shrink-0 group-hover:bg-enterprise-purple/20 transition-colors duration-300">
                    <audience.icon className="w-6 h-6 text-enterprise-purple" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-lg mb-1 group-hover:text-enterprise-purple transition-colors duration-300">{audience.title}</h3>
                    <p className="text-sm text-muted-foreground">{audience.description}</p>
                  </div>
                </>
              );

              if (audience.link) {
                return (
                  <a 
                    key={index} 
                    href={audience.link}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="group bg-card border border-border rounded-2xl p-6 hover:border-enterprise-purple/30 hover:shadow-lg hover:scale-[1.02] transition-all duration-300 flex items-start gap-4 cursor-pointer"
                  >
                    {CardContent}
                  </a>
                );
              }

              return (
                <div key={index} className="group bg-card border border-border rounded-2xl p-6 hover:border-enterprise-purple/30 hover:shadow-lg hover:scale-[1.02] transition-all duration-300 flex items-start gap-4 cursor-pointer">
                  {CardContent}
                </div>
              );
            })}
          </div>

          <div className="mt-12 text-center">
            <p className="text-muted-foreground mb-4">
              <Building2 className="w-4 h-4 inline mr-2" />
              Need QCard for your team?
            </p>
            <Button size="lg" className="bg-enterprise-purple hover:bg-enterprise-purple-dark text-white text-xl px-12 py-8 font-bold shadow-xl shadow-enterprise-purple/30" onClick={() => setIsEnterpriseExpanded(!isEnterpriseExpanded)}>
              Enterprise Plan Available
              <ChevronDown className={`ml-3 w-6 h-6 transition-transform duration-300 ${isEnterpriseExpanded ? 'rotate-180' : ''}`} />
            </Button>
            
            {/* Expanding Enterprise Content */}
            <div className={`overflow-hidden transition-all duration-500 ease-in-out ${isEnterpriseExpanded ? 'max-h-[2000px] opacity-100 mt-12' : 'max-h-0 opacity-0 mt-0'}`}>
              <div className="max-w-3xl mx-auto text-center mb-12">
                <h3 className="text-2xl md:text-3xl font-bold mb-4">
                  Designed for <span className="text-enterprise-purple">Enterprises</span>, from the Ground Up
                </h3>
                <p className="text-lg text-muted-foreground">
                  QCard provides the cutting edge features, infrastructure, security and support that enterprises need.
                </p>
              </div>
              
              <div className="grid md:grid-cols-2 gap-6 max-w-5xl mx-auto text-left">
                {enterpriseFeatures.map((feature, index) => <div key={index} className="bg-card border border-border rounded-2xl p-6 hover:shadow-lg transition-all duration-300">
                    <div className={`w-14 h-14 rounded-2xl ${feature.color} flex items-center justify-center mb-4`}>
                      <feature.icon className="w-7 h-7" />
                    </div>
                    <h4 className="text-lg font-bold mb-3">{feature.title}</h4>
                    <ul className="space-y-2">
                      {feature.items.map((item, itemIndex) => <li key={itemIndex} className="flex items-start gap-2 text-muted-foreground text-sm">
                          <span className="w-1.5 h-1.5 rounded-full bg-enterprise-purple mt-1.5 flex-shrink-0" />
                          <span>{item}</span>
                        </li>)}
                    </ul>
                  </div>)}
              </div>
              
              <div className="mt-10">
                <Button size="lg" className="bg-enterprise-purple hover:bg-enterprise-purple-dark text-white text-lg px-8 py-6 font-semibold" asChild>
                  <a href="mailto:info@qcardenterprise.com">
                    Contact Sales
                    <ArrowRight className="ml-2 h-5 w-5" />
                  </a>
                </Button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* SECTION 3: Value of Professional Digital Identity */}
      <section className="py-20 md:py-28 bg-muted/30">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold mb-4">
              Your Professional <span className="text-gradient">Digital Identity</span>
            </h2>
            <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
              More than a business card — it's how you present yourself to the world.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8 max-w-6xl mx-auto">
            {benefits.map((benefit, index) => (
              <div key={index} className="text-center">
                <div className="w-16 h-16 rounded-2xl bg-enterprise-purple/10 flex items-center justify-center mx-auto mb-6">
                  <benefit.icon className="w-8 h-8 text-enterprise-purple" />
                </div>
                <h3 className="font-semibold text-xl mb-3">{benefit.title}</h3>
                <p className="text-muted-foreground">{benefit.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* SECTION 4: How It Works */}
      <section className="py-20 md:py-28 bg-background">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold mb-4">
              How It <span className="text-gradient">Works</span>
            </h2>
            <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
              Get your professional digital card in under 60 seconds.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8 max-w-5xl mx-auto">
            {steps.map((step, index) => (
              <div key={index} className="relative text-center">
                {index < steps.length - 1 && <div className="hidden md:block absolute top-12 left-[60%] w-[80%] h-[2px] bg-gradient-to-r from-enterprise-purple/50 to-transparent" />}
                <div className="w-24 h-24 rounded-full bg-gradient-to-br from-enterprise-purple to-enterprise-purple-dark flex items-center justify-center mx-auto mb-6 text-white text-3xl font-bold shadow-lg shadow-enterprise-purple/30">
                  {step.step}
                </div>
                <h3 className="font-semibold text-xl mb-3">{step.title}</h3>
                <p className="text-muted-foreground">{step.description}</p>
              </div>
            ))}
          </div>

          <div className="mt-12 text-center">
            <Button size="lg" className="bg-enterprise-purple hover:bg-enterprise-purple-dark text-white text-lg px-8 py-6 font-semibold shadow-lg shadow-enterprise-purple/30" onClick={() => document.getElementById('signup-section')?.scrollIntoView({
            behavior: 'smooth'
          })}>
              Get Started Free
              <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
          </div>
        </div>
      </section>

      {/* SECTION 5: Why Choose QCard */}
      <section className="py-20 md:py-28 bg-muted/30">
        <div className="container mx-auto px-4">
          <div className="grid lg:grid-cols-2 gap-16 items-center max-w-6xl mx-auto">
            <div>
              <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold mb-6">
                Why Choose <span className="text-gradient">QCard?</span>
              </h2>
              <p className="text-xl text-muted-foreground mb-8">
                In a world of forgettable first impressions, QCard helps you stand out and stay connected.
              </p>
              
              <div className="space-y-4">
                {["Be remembered — not just another card in the drawer", "Be searchable — your digital profile lives online", "Look professional — polished, modern, and credible", "Stand out — make every interaction count", "Replace outdated paper cards — go digital, go green"].map((item, index) => (
                  <div key={index} className="flex items-center gap-3">
                    <div className="w-6 h-6 rounded-full bg-enterprise-purple/20 flex items-center justify-center flex-shrink-0">
                      <Check className="w-4 h-4 text-enterprise-purple" />
                    </div>
                    <span className="text-lg">{item}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Old Way vs QCard */}
            <div className="grid gap-6">
              <div className="bg-card border border-border rounded-2xl p-6">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 rounded-full bg-destructive/10 flex items-center justify-center">
                    <span className="text-destructive font-bold">✕</span>
                  </div>
                  <h3 className="font-semibold text-lg">The Old Way</h3>
                </div>
                <ul className="space-y-2 text-muted-foreground">
                  <li>• Paper cards get lost or thrown away</li>
                  <li>• Manual contact entry (if they bother)</li>
                  <li>• Outdated info after a job change</li>
                  <li>• No way to share digitally</li>
                  <li>• Limited first impression</li>
                </ul>
              </div>

              <div className="bg-gradient-to-br from-enterprise-purple/5 to-enterprise-purple/10 border border-enterprise-purple/20 rounded-2xl p-6">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 rounded-full bg-enterprise-purple/20 flex items-center justify-center">
                    <Check className="w-5 h-5 text-enterprise-purple" />
                  </div>
                  <h3 className="font-semibold text-lg">The QCard Way</h3>
                </div>
                <ul className="space-y-2">
                  <li className="flex items-center gap-2">
                    <Check className="w-4 h-4 text-enterprise-purple flex-shrink-0" />
                    <span>Saved directly to their phone</span>
                  </li>
                  <li className="flex items-center gap-2">
                    <Check className="w-4 h-4 text-enterprise-purple flex-shrink-0" />
                    <span>One-tap contact saving</span>
                  </li>
                  <li className="flex items-center gap-2">
                    <Check className="w-4 h-4 text-enterprise-purple flex-shrink-0" />
                    <span>Always up-to-date</span>
                  </li>
                  <li className="flex items-center gap-2">
                    <Check className="w-4 h-4 text-enterprise-purple flex-shrink-0" />
                    <span>Share via QR, link, or text</span>
                  </li>
                  <li className="flex items-center gap-2">
                    <Check className="w-4 h-4 text-enterprise-purple flex-shrink-0" />
                    <span>Professional, memorable impression</span>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* SECTION 6: Competitor Comparison - Lazy loaded */}
      <Suspense fallback={<div className="py-20 bg-background"><div className="container mx-auto px-4 text-center text-muted-foreground">Loading comparison...</div></div>}>
        <ComparisonGridSection />
      </Suspense>


      {/* SECTION 7: Free Tier Callout */}
      <section className="py-20 md:py-28 bg-gradient-to-br from-enterprise-purple/5 via-background to-enterprise-purple/10">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-enterprise-purple/10 text-enterprise-purple text-sm font-semibold mb-6 border border-enterprise-purple/20">
              <Star className="w-4 h-4" />
              Free Tier. Full Capability.
            </div>
            
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold mb-6">
              This Isn't a "Basic" Version.<br />
              <span className="text-gradient">It's the Real Thing.</span>
            </h2>
            
            <p className="text-xl text-muted-foreground mb-8 max-w-2xl mx-auto">
              We believe everyone deserves a professional digital presence. That's why QCard Free includes 
              every feature you need — no artificial limitations, no paywalls for essentials.
            </p>

            <div className="flex flex-wrap justify-center gap-4 mb-8">
              {["Full Profile", "Unlimited Sharing", "QR Code", "Personal URL", "Contact Saving", "All Links"].map((item, index) => (
                <div key={index} className="flex items-center gap-2 bg-card border border-border rounded-full px-4 py-2">
                  <Check className="w-4 h-4 text-green-600" />
                  <span className="text-sm font-medium">{item}</span>
                </div>
              ))}
            </div>

            <div>
              <Button size="lg" className="bg-enterprise-purple hover:bg-enterprise-purple-dark text-white text-lg px-10 py-6 font-semibold shadow-lg shadow-enterprise-purple/30" onClick={() => document.getElementById('signup-section')?.scrollIntoView({
              behavior: 'smooth'
            })}>
                Create Your Free QCard
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* SECTION 8: Testimonials */}
      <section className="py-20 md:py-28 bg-muted/30">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold mb-4">
              What Professionals <span className="text-gradient">Are Saying</span>
            </h2>
          </div>

          {/* Desktop: Grid of all 4 testimonials */}
          <div className="hidden md:grid md:grid-cols-2 gap-6 max-w-4xl mx-auto">
            {testimonials.map((testimonial, index) => (
              <a 
                key={index} 
                href={testimonial.link} 
                target="_blank" 
                rel="noopener noreferrer" 
                className="block overflow-hidden rounded-2xl shadow-xl hover:shadow-2xl transition-all duration-300 transform hover:scale-[1.02]"
              >
                <img src={testimonial.image} alt={`${testimonial.name} from ${testimonial.company} testimonial`} className="w-full h-auto" width={600} height={600} loading="lazy" decoding="async" />
              </a>
            ))}
          </div>

          {/* Mobile: Carousel */}
          <div className="md:hidden max-w-sm mx-auto px-8">
            <Carousel opts={{
            align: "center",
            loop: true
          }} plugins={[Autoplay({
            delay: 4000,
            stopOnInteraction: false,
            stopOnMouseEnter: true
          })]} className="w-full">
              <CarouselContent>
                {testimonials.map((testimonial, index) => <CarouselItem key={index}>
                    <div className="p-2">
                      <a href={testimonial.link} target="_blank" rel="noopener noreferrer" className="block overflow-hidden rounded-2xl shadow-xl hover:shadow-2xl transition-all duration-300 transform hover:scale-[1.02]">
                        <img src={testimonial.image} alt={`${testimonial.name} from ${testimonial.company} testimonial`} className="w-full h-auto" width={600} height={600} loading="lazy" decoding="async" />
                      </a>
                    </div>
                  </CarouselItem>)}
              </CarouselContent>
              <CarouselPrevious className="left-0 bg-background/80 hover:bg-background border-border" />
              <CarouselNext className="right-0 bg-background/80 hover:bg-background border-border" />
            </Carousel>

            {/* Dots indicator - mobile only */}
            <div className="flex justify-center gap-2 mt-6">
              {testimonials.map((_, index) => <div key={index} className="w-2 h-2 rounded-full bg-enterprise-purple/30 transition-colors" />)}
            </div>
          </div>
        </div>
      </section>

      {/* SECTION 9: Final CTA */}
      <section className="py-24 md:py-32 bg-gradient-to-br from-foreground to-foreground/90 text-background">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold mb-6">
            Create Your Free QCard — It Only Takes 60 Seconds.
          </h2>
          <p className="text-xl text-background/70 mb-10 max-w-2xl mx-auto">
            No credit card required. All features included. Start making lasting impressions today.
          </p>
          
          <div>
            <Button size="lg" className="bg-enterprise-purple hover:bg-enterprise-purple-dark text-white text-xl px-12 py-8 font-semibold shadow-xl shadow-enterprise-purple/40" onClick={() => document.getElementById('signup-section')?.scrollIntoView({
            behavior: 'smooth'
          })}>
              Create Your Free QCard
              <ArrowRight className="ml-3 h-6 w-6" />
            </Button>
          </div>

          <div className="flex items-center gap-8 justify-center mt-10 text-sm text-background/60">
            <div className="flex items-center gap-2">
              <Clock className="w-4 h-4" />
              <span>60-second setup</span>
            </div>
            <div className="flex items-center gap-2">
              <Check className="w-4 h-4" />
              <span>No credit card</span>
            </div>
            <div className="flex items-center gap-2">
              <Star className="w-4 h-4" />
              <span>All features free</span>
            </div>
          </div>
        </div>
      </section>

      </div>
    </MotionConfig>
  );
};
export default HomepageEnterprise;